import { useEffect, useState } from "react";
import { CreateCartPayload } from "shared/api/middleware-api-client";
import { STORAGE_SALE_LOCATION } from "shared/config";
import {
  STANDARD_PRODUCT_PAYMENT_METHOD,
  STANDARD_PRODUCT_PAYMENT_TYPE,
} from "shared/utils/standard-product";

export interface Cart {
  getCart: () => CreateCartPayload;
  getDefaultCart: () => CreateCartPayload;
  setCartToDefault: () => void;
  updateCart: (data: CreateCartPayload) => void;
}

export const STORAGE_CART_KEY = "gewoongers_cart";
export const useCart = (): Cart => {
  const [cart, setCart] = useState<CreateCartPayload | null>(null);
  useEffect(() => {
    setCart(getCart());
  }, []);

  useEffect(() => {
    if (!cart) return;
    if (!cart.standardProducts) {
      setCart(getDefaultCart());
    }
  }, [cart]);

  const getCart = (): CreateCartPayload => {
    let localCart = localStorage.getItem(STORAGE_CART_KEY);
    if (localCart) {
      return fixLocalCart(JSON.parse(localCart) as CreateCartPayload);
    }

    return getDefaultCart();
  };

  const fixLocalCart = (localCart: CreateCartPayload): CreateCartPayload => {
    let hasUpdatedLocalCart = false;
    if (!localCart.standardProducts) {
      localCart.standardProducts = [];
      hasUpdatedLocalCart = true;
    }

    if (!localCart.delivery) {
      localCart.delivery = { type: "", date: "" };
      hasUpdatedLocalCart = true;
    }

    if (hasUpdatedLocalCart) {
      updateCart(localCart);
    }

    return localCart;
  };

  const getDefaultCart = (): CreateCartPayload => {
    return {
      paymentMethod: {
        data: {
          issuer: "",
        },
        method: "",
      },
      totalPrice: 0,
      addressValid: false,
      discountCode: "",
      email: "",
      emailAddress: "",
      address: {
        emailAddress: "",
        invoiceAddress: {
          email: "",
          VAT: "",
          company: "",
          location: {
            type: "",
          },
          municipality: "",
          reference: "",
          city: "",
          province: "",
          houseNumber: "",
          phone: "",
          street: "",
          zip: "",
        },
        shippingAddress: {
          location: {
            type: "",
          },
          municipality: "",
          province: "",
          city: "",
          firstName: "",
          houseNumber: "",
          lastName: "",
          phone: "",
          street: "",
          zip: "",
          company: "",
          reference: "",
          VAT: "",
          email: "",
        },
      },
      products: [],
      supplyProducts: [],
      standardProducts: [],
      warrantyPackage: undefined,
      saleLocation: localStorage.getItem(STORAGE_SALE_LOCATION) || "",
      shoppingCartId: "",
      subscribeToNewsletter: false,
      sampleSale: false,
      sampleSaleMeasurement: "",
      sampleSaleRabbetSize: "",
      sampleSaleReferenceNumber: "",
      sampleSaleRotation: "",
      standardProductPayment: {
        paymentBank: 0,
        paymentMethod: STANDARD_PRODUCT_PAYMENT_METHOD,
        paymentType: STANDARD_PRODUCT_PAYMENT_TYPE,
      },
      pickupWarehouse: "",
      delivery: {
        type: "pick-up", // dhl || gerse-bezorgdienst || pick-up
        date: "", // date
      },
      bumbalFlow: "",
      withAssembly: false,
      isSelfAssembly: undefined,
      discountRecords: [],
      internalNotes: [],
    };
  };

  const updateCart = (data: CreateCartPayload) => {
    data.totalPrice = data.products.reduce((prevPrice, currProduct) => {
      return prevPrice + (currProduct.totalPrice || 0);
    }, 0);

    if (data.standardProducts.length > 0) {
      data.totalPrice += data.standardProducts.reduce(
        (prevPrice, currPanel) =>
          prevPrice + currPanel.price * (currPanel.amount || 1),
        0,
      );

      data.warrantyPackage = undefined;
    }

    if (data.warrantyPackage) {
      data.totalPrice += data.warrantyPackage.price || 0;
    }

    let updatedCart = JSON.stringify(data);

    localStorage.setItem(STORAGE_CART_KEY, updatedCart);

    setCart(data);
  };

  const setCartToDefault = () => {
    const defaultCart = getDefaultCart();
    setCart(defaultCart);
    localStorage.setItem(STORAGE_CART_KEY, JSON.stringify(defaultCart));
  };

  return {
    getCart,
    getDefaultCart,
    setCartToDefault,
    updateCart,
  };
};
